// FLAG:useDocuAi — additive/pure analysis-state helpers. No flag check inside (it's a Layer-A
// utility); it is only *consumed* by renderers that are themselves gated on useDocuAi, so flag-OFF
// never reaches it. Derives the invoice analysis state of a file (analyzing / review / invalid /
// complete …) from the data the file already carries (metaData.ocr, documentInvoiceDetails,
// validationResult).
import lang from '@/lang'
import checkOcrValues from '@/modules/document/_helpers/checkOcrValues'
import { isDesktopOwnedFile } from '@/modules/file/desktop-owned-file'

export const INVOICE_ANALYSIS_STATE = {
  ANALYZING: 'analyzing',
  // FLAG:useDocuAi — non-terminal transient retry (plan docuai-analysis-retrying): the
  // analysis hit an infrastructure problem and DocuAI re-drives it itself. Still an
  // in-flight state (analysisInProgress true), but rendered calm ("delayed — will be
  // retried automatically") instead of the plain blink.
  RETRYING: 'retrying',
  COMPLETE: 'complete',
  FAILED: 'failed',
  INVALID: 'invalid',
  PARTIAL: 'partial',
  REVIEW: 'review',
  VALID: 'valid',
  // FLAG:useDocuAi — terminal outcomes without a result (plans/ausgaben-analyse-terminal-states):
  // ABORTED = watchdog timeout (subtle, NOT red — a timeout is not an observed failure;
  // observed technical failures use FAILED (amber AI+!, distinct from invalid red),
  // NO_INVOICE = the analysis terminated and recognised no invoice in the document.
  ABORTED: 'aborted',
  NO_INVOICE: 'noInvoice',
  // FLAG:useDocuAi — terminal outcomes for documents that never produce an analysis result
  // at all, carried by metaData.analysisEligibility (DMS `indexingFinished`):
  //   NOT_APPLICABLE  — video, audio, CAD, opaque binary. Nothing downstream can read them,
  //                     so no analysis is attempted. This is a DECISION, not a failure, and
  //                     it is shown precisely so it stops being indistinguishable from an
  //                     analysis that silently never started.
  //   CONTENT_INDEXED — an archive. Its members are searchable; the container itself has no
  //                     classification and never will. Independent of which engine produced
  //                     the text, so two identical zips can no longer show different tiles.
  NOT_APPLICABLE: 'notApplicable',
  CONTENT_INDEXED: 'contentIndexed',
}

// FLAG:useDocuAi — the SERVER-side terminal failure verdicts a receipt-list RECORD can carry in
// realState/state (plans/ausgaben-analyse-terminal-states; since plan
// omxweb-draft-receipt-instead-of-review-row T8 projected from the record's LINKED file by
// getExpensesSSR / getRevenuesSSR, `analysisOutcome`, over a 'toAnalyze' / 'draft' record that never
// got a real result). Such a record has NO documentInvoiceDetails behind it — nothing to review, only
// the upload placeholders; the receipt lists open it in its edit form for the manual entry:
//   ANALYSIS_ABORTED — the watchdog's neutral timeout terminal (subtle, NOT red:
//                      a timeout is not an observed failure)
//   NO_INVOICE_FOUND — the analysis terminated and found no invoice in the document
// ('analyzeError' rows reuse the existing expenseStates.ANALYZE_ERROR red rendering.)
export const REVIEW_ROW_TERMINAL_STATE = {
  ANALYSIS_ABORTED: 'analysisAborted',
  NO_INVOICE_FOUND: 'noInvoiceFound',
}

// FLAG:useDocuAi — client absolute backstop (Layer 1 of the termination guarantee, see
// docs/bidirectional-comm-hardening-plan.md). The AI badge must NEVER blink endlessly, even if
// every backend signal is lost (terminal event dropped, ws blip during analysis, DocuAI hang,
// watchdog off). Once a tile has been analyzing this long we HALT the blink unconditionally,
// independent of any server signal.
//
// **45 seconds, established 2026-08-12.** This used to be 5 minutes, aligned with the server
// watchdog's CODE default. It was intended as an emergency brake but was experienced as normal:
// on a stuck run (Issue 04c0203b), the tile blinked for a measured 2 min 41 s — the 120 s watchdog
// threshold plus up to 60 s scheduling delay — and with the default it would have blinked for more
// than 5 minutes. A browser full of blinking rows no longer conveys any information.
//
// The number follows observations, not intuition: locally measured `last_run_ms` values are
// 0.3–7.3 s, and the slowest documented run (29-page OCR, Casebook 0073) took 21.4 s.
// Thus, 45 s leaves ample headroom over what a real analysis requires.
//
// A run that DOES take longer is not broken by this: the verdict is NEUTRAL (no red, no error
// badge), and a real result arriving late overrides it (processInvoiceAnalysisResult clears
// analysisTimedOut). The tile then briefly says nothing before switching to „Zu prüfen" — rather
// than blinking for minutes.
//
// F21 invariant: this value MUST be >= the server watchdog threshold
// DOCUAI_ANALYSIS_STUCK_MS, whose code default was reduced to 45 s in the same change
// (api/src/worker/queue/dms/reconcileStuckAnalysis.ts). The server additionally needs its polling
// interval (DOCUAI_ANALYSIS_WATCHDOG_INTERVAL_MS, now 15 s by default), so the DB catches up within
// ~45–60 s. During that intervening window the display remains consistent: the cap is anchored to
// `metaData.analysisStartedAt`, a SERVER timestamp — a reload calculates the same age limit and
// does not start blinking again. Only the fallback to `firstObservedAnalyzingAt` (when the timestamp
// is missing) can be reset by a reload; that is why the invariant remains necessary.
export const ANALYZING_MAX_MS = 45 * 1000

// Client-side fallback start timestamps: the instant a tile was first observed analyzing on THIS
// client. We bound the blink by metaData.analysisStartedAt when it is present (so an old/stale
// start caps immediately — a browser reload then shows the real, non-blinking state), and fall
// back to firstObserved only when analysisStartedAt is absent (e.g. the analysisStarted event was
// the one that got lost, or the row was hydrated from a source without it) so the blink still
// terminates. Bounded: entries are dropped the moment the file leaves the analyzing state.
const firstObservedAnalyzingAt = new Map()

const analyzingStartMs = (file) => {
  const startedAt = file?.metaData?.analysisStartedAt
  const serverMs = startedAt ? new Date(startedAt).getTime() : null
  if (serverMs != null && Number.isFinite(serverMs)) return serverMs

  const id = file?.id
  if (id == null) return null
  let observed = firstObservedAnalyzingAt.get(id)
  if (observed == null) {
    observed = Date.now()
    firstObservedAnalyzingAt.set(id, observed)
  }
  return observed
}

export const forgetAnalyzingBackstop = (id) => {
  if (id != null) firstObservedAnalyzingAt.delete(id)
}

// FLAG:useDocuAi — reconnect/visibility recovery must also include rows whose synchronous
// terminal patch already cleared analysisInProgress while a stale provider placeholder survived.
// Keeping the predicate beside the state vocabulary prevents the list from defining a narrower
// meaning of "still looks active" than the badge itself.
export const isAnalysisStateResyncCandidate = file =>
  file?.metaData?.analysisInProgress === true
  || file?.metaData?.ocr?.isRunning === true

// FLAG:useDocuAi — the same age cap for list surfaces that carry analysisStartedAt as a
// row COLUMN (getExpensesSSR review rows) instead of metaData: true once the analysing
// state is older than ANALYZING_MAX_MS, so the expense blink halts client-side even
// with the server watchdog off. Without the stamp there is nothing to bound against —
// the server watchdog and the FILE:ANALYSED-driven refetch remain the guarantees there.
export const isAnalysisStartStale = (analysisStartedAt) => {
  const startedMs = analysisStartedAt ? new Date(analysisStartedAt).getTime() : NaN
  return Number.isFinite(startedMs) && (Date.now() - startedMs) > ANALYZING_MAX_MS
}

export const isValidValidationValue = value => value === true || value === 'true' || value === 1 || value === '1'

export const isReviewedValue = value => value === true || value === 'true' || value === 1 || value === '1'

export const isUnreviewedValue = value => value === false || value === 'false' || value === 0 || value === '0'

// FLAG:useDocuAi — THIRD source: `invoiceValidationState` ('valid' | 'invalid' | null), the
// projection already carried by the expense/revenue lists and included in the document list since
// 2026-08-16 (routes/documents/_helpers/makeQuery.js, from the same verdict helper as the review
// component). Without it, the verdict was not known on the TILE at all: a list row carries neither
// `validationResult` nor `documentInvoiceDetails` — only `metaData` — so a reviewed but invalid
// e-invoice appeared green as „Geprüft" via `metaData.invoiceAnalysisReviewed`. The precedence
// “invalid beats reviewed” was correct, yet it just had no input here. Purely additive: if the field
// is missing (flag-OFF, or a surface without the projection), only the other two sources count, as
// before.
export const hasInvalidValidationResult = file =>
  file?.hasInvalidValidationResult
  || file?.invoiceValidationState === 'invalid'
  || (!!file?.validationResult && !isValidValidationValue(file.validationResult.valid))

export const hasXmlInvoiceAnalysis = file =>
  file?.metaData?.ocr?.type === 'xmlExtraction'
  || file?.metaData?.ocr?.invoiceAnalysisSource === 'xml'
  || file?.documentInvoiceDetails?.reviewSource?.source === 'xml'
  || !!file?.validationResult
  || !!file?.xmlValidationResult

// FLAG:useDocuAi — a TERMINAL MARKER is not an analysis result. When the analysis finished and
// recognised no invoice, `processAnalysisDone` (and `analyzeFile` for empty legacy results) writes
// `ocr = { noInvoiceFound: true, isRunning: false }`. That object exists but carries no extracted
// data — and `hasLegacyOcrAnalysis` used to answer "yes, legacy OCR analysis" for it purely because
// `ocr` was present. Consequences, both reported on a DocuAI-recognised contract: the tile fell
// through to the yellow "Zu prüfen" clip, and the badge branch preferred the review badge over the
// static AI badge. So: exclude the marker-only shape.
const hasOnlyTerminalOcrMarker = file => file?.metaData?.ocr?.noInvoiceFound === true

export const hasLegacyOcrAnalysis = file =>
  !!file?.metaData?.ocr && !hasXmlInvoiceAnalysis(file) && !hasOnlyTerminalOcrMarker(file)

// FLAG:useDocuAi — the provider stamp every converter writes into metaData.ocr
// (api/src/constants.ts ANALYSES_PROVIDERS: 'gini' | 'natif' | 'docuai'). There is no shared
// frontend constant for it, so it is named here rather than spelled out at each use.
const DOCUAI_OCR_PROVIDER = 'docuai'

// FLAG:useDocuAi — "DocuAI has actually looked at this document".
//
// The states, badges and doors of this feature describe a DocuAI lifecycle. A document analysed
// BEFORE the tenant was switched on carries a gini/natif result and never had one: it has no
// review row to open, no terminal to wait for and no DocuAI data to fetch. Showing it the new
// badge promises a review the click cannot deliver, which is exactly what the "Zu prüfen" clip on
// an untouched legacy document looked like.
//
// Several markers, not just the stamp: the stamp is the direct evidence, the lifecycle markers
// cover a run that is still in flight (no result written yet), and a review row covers a document
// whose ocr was superseded. Any ONE of them means DocuAI owns this document — and the moment a
// user hits „Erneut analysieren", the DocuAI run overwrites metaData.ocr and the new surfaces
// start applying on their own. No migration, no backfill.
export const hasDocuAiAnalysis = file =>
  file?.metaData?.ocr?.type === DOCUAI_OCR_PROVIDER
  || !!file?.metaData?.aiProcessed
  || file?.metaData?.analysisInProgress === true
  || !!file?.metaData?.analysisStartedAt
  || !!file?.documentInvoiceDetails?.reviewStatus

export const hasReviewedInvoiceAnalysis = (file) => {
  if (file?.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.COMPLETE
    || file?.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.PARTIAL) {
    return true
  }

  if (isReviewedValue(file?.metaData?.invoiceAnalysisReviewed) || isReviewedValue(file?.metaData?.ocr?.reviewed)) {
    return true
  }

  if (isUnreviewedValue(file?.metaData?.invoiceAnalysisReviewed) || isUnreviewedValue(file?.metaData?.ocr?.reviewed)) {
    return false
  }

  return false
}

// FLAG:useDocuAi — a DocuAI invoice whose review the operator has not yet
// confirmed: documentInvoiceDetails is pre-filled with reviewStatus 'review' and
// nothing has marked it reviewed. Used to keep the review fields editable/yellow
// and to let the operator SAVE (confirm) the drawer even without field edits.
export const isPendingInvoiceReview = file =>
  file?.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.REVIEW
  && !hasReviewedInvoiceAnalysis(file)

// FLAG:useDocuAi — the statuses that mean "already reviewed", i.e. saving again is a RE-review.
// Owned here (the module that owns the states) so the drawer gate below and the re-review save
// hook (use-re-review-save.ts, which re-exports this) cannot drift apart.
const REVIEWED_INVOICE_STATUSES: string[] = [INVOICE_ANALYSIS_STATE.COMPLETE, INVOICE_ANALYSIS_STATE.PARTIAL]

export const isReReviewStatus = (reviewStatus?: string | null) => REVIEWED_INVOICE_STATUSES.includes(reviewStatus ?? '')

// FLAG:useDocuAi — DocuAI classifications that must NEVER be offered for review, whatever their
// review status says. DocuAI recognises contracts (and extracts them into its own contract table),
// but orgaMAX Web has no contract feature: there is nothing to review, nothing to book, and no
// expense to create from one. Today a contract never reaches the review anyway — it produces no
// invoice extraction, so no `documentInvoiceDetails` is written — but that is a property of the
// current DocuAI pipeline, not a guarantee. This is the guarantee.
//
// Keyed on the persisted CLASSIFICATION (`metaData.docType`, written by processAnalysisDone from
// the DMS event), deliberately NOT on the "Art" tag: the type is user-editable, so tagging a
// contract as "Rechnungseingang" would otherwise unlock a review for a document with no invoice
// data behind it.
const CLASSIFICATIONS_WITHOUT_REVIEW: string[] = ['contract']

export const isReviewBlockedClassification = (file) =>
  CLASSIFICATIONS_WITHOUT_REVIEW.includes(`${file?.metaData?.docType ?? ''}`.toLowerCase())

// FLAG:useDocuAi — the analysis TERMINATED and left nothing to review: DocuAI classified the
// document as something other than an invoice (house rules, a contract, a letter …). This is a
// RESULT, not a failure — which is exactly why it needs its own answer. Two markers say it:
//   metaData.ocr.noInvoiceFound — the honest terminal marker, written by processAnalysisDone for
//     the DMS `analysisFinished` event (classification says "not an invoice", no invoice analysis
//     ever runs) AND by analyzeFile when an invoice analysis comes back empty.
//   a review-blocked classification (contract) on a document whose analysis is no longer running —
//     the belt-and-braces half, so a future provider that sets docType without the ocr marker
//     still terminates the wait.
// Deliberately NOT keyed on "no documentInvoiceDetails": that is also true for the whole (often
// 15–20s) analysis window, which is the state this one has to be distinguishable FROM.
export const isAnalysedNonInvoice = (file) => {
  if (file?.metaData?.ocr?.noInvoiceFound === true) { return true }
  if (file?.metaData?.analysisInProgress === true) { return false }
  return isReviewBlockedClassification(file)
}

// FLAG:useDocuAi — the document-derived half of "may this document be re-analysed under a type
// the user asserted?" ("Als Rechnung analysieren"). The caller ANDs it with its own permission and
// Art gates, exactly like canActOnInvoiceReview — this half answers only the question that depends
// on the DOCUMENT's state, and owns it so the menu entry and any future entry point cannot drift.
//
// The case it exists for: DocuAI's analysis SUCCEEDED but classified the document as a non-invoice
// (a Telekom "Mobilfunkreport" reads as Abrechnung), so no invoice schema ran, there is no review
// and no result — and therefore no other door in the product opens. Without this the user can set
// the Art to "Rechnungseingang" by hand and nothing anywhere will act on it.
//
// Each clause is a refusal, not decoration:
//   isAnalysedNonInvoice — the analysis TERMINATED without an invoice. This is the whole
//     precondition; it also excludes the (often 15–20s) in-flight window.
//   analysisInProgress   — belt-and-braces for the one shape isAnalysedNonInvoice answers `true`
//     for while a run is live: a stale ocr.noInvoiceFound marker from the PREVIOUS run, which is
//     still present until the new terminal lands. Offering a second run there would double-charge.
//   reviewStatus         — a review exists ⇒ there IS invoice data; this is not that case, and a
//     re-analysis must never be able to overwrite reviewed values.
//   isAssigned           — already booked onto an expense/revenue, same reason, one step further.
// Deliberately NOT gated on isReviewBlockedClassification: a contract that is really an invoice is
// precisely the misclassification this corrects. It unlocks no review by itself — it produces
// invoice data or it does not, and the normal gates then decide.
export const isReanalysableNonInvoice = (file) =>
  !file?.documentInvoiceDetails?.reviewStatus
  && !file?.isAssigned
  && file?.metaData?.analysisInProgress !== true
  && isAnalysedNonInvoice(file)

// FLAG:useDocuAi — the expense attached to this document. The drawer receives it as an OBJECT
// (`serialize` sets `data.expense` from the full row, including `state`); other callers have the
// belongsToMany collection `expenses` (array) — both shapes occur in the client, see
// `document-assignment-router.tsx` („const { expenses = [], …, expense } = document"). Keep this in
// one place so the next lookup does not become a third variant.
const assignedExpense = (file) => {
  if (Array.isArray(file?.expenses)) { return file.expenses[0] }
  return Array.isArray(file?.expense) ? file.expense[0] : file?.expense
}

// FLAG:useDocuAi — draft states of an expense. Anything beyond them (starting at „Offen") is
// registered in bookingApi because the final review save books immediately.
const EXPENSE_DRAFT_STATES: string[] = ['draft', 'toAnalyze', 'analyzeError']

// FLAG:useDocuAi — “does this receipt now have an expense attached that has left draft status?”
// From that point on, the exact phrase “the linked expense (draft)” in the reset dialog is no
// longer accurate, and the reset does not roll back accounting either — both facts belong in the
// confirmation text.
//
// Only for choosing the TEXT. Permission is still decided solely by the server
// (RESETTABLE_EXPENSE_STATES), so client and server
// cannot define two boundaries that drift apart.
// If the status is missing (a caller serializing the expense more sparsely), the result falls back
// to `false` and therefore to the previous text — less elegant, but never more wrong than today.
export const hasBookedAssignedExpense = (file) => {
  const state = assignedExpense(file)?.state
  return !!state && !EXPENSE_DRAFT_STATES.includes(state)
}

// FLAG:useDocuAi — „Erneut analysieren": may a FRESH DocuAI run be offered for this document?
// The gap behind this: once a receipt's analysis successfully identifies an invoice, it is frozen
// — `analyze` is flag-OFF-only, `retryFailedAnalysis` requires a failure,
// `isReanalysableNonInvoice` requires "no invoice detected", and reset only re-derives from the
// OLD result. No later DocuAI improvement can reach it.
//
// Deliberately NOT gated on `reviewStatus`/`isAssigned` — unlike
// `isReanalysableNonInvoice`, and that difference is what makes it safe: this run overwrites
// nothing. It only refreshes the stored analysis;
// the F3 guard in `upsertFromAnalysis` preserves
// the reviewed values server-side, and applying them is an explicit „In Prüfung zurücksetzen".
//
// Every clause is a refusal, not decoration:
// analysisInProgress / ocr.isRunning — a run is already in flight. A second would burn the
// tenant's MONTHLY quota on a single document (the DMS rejects it too, but a menu entry that is
// guaranteed to fail should not be offered in the first place).
// hasAnalysisResult — there is something to refresh at all. A document without a result belongs
// to the other entry points (initial analysis, failure retry, „Als Rechnung analysieren").
// isReviewBlockedClassification — orgaMAX Web has no review for a contract; nobody would consume
// a fresh result there.
export const isRefreshableAnalysedInvoice = (file) => {
  if (file?.metaData?.analysisInProgress === true) { return false }
  // FLAG:useDocuAi — only a DocuAI run may hold this door shut. A bare `ocr.isRunning` from a
  // legacy natif/gini run whose result never arrived (a lost `natifReady`) has no terminal, no
  // watchdog (reconcileStuckAnalysis keys on analysisInProgress) and no way back — read as "still
  // running" it locked the document forever, and the user could not even re-trigger it. A real
  // DocuAI run still blocks: it carries the provider stamp and its own lifecycle markers.
  if (file?.metaData?.ocr?.isRunning === true && hasDocuAiAnalysis(file)) { return false }
  if (isReviewBlockedClassification(file)) { return false }
  const hasAnalysisResult = !!file?.documentInvoiceDetails
    || hasXmlInvoiceAnalysis(file)
    || hasLegacyOcrAnalysis(file)
  return hasAnalysisResult
}

// FLAG:useDocuAi — the drawer's Details tab embeds the central review component for
// every invoice in a writable folder that HAS a review to show: the initial review (reviewStatus
// 'review') AND a re-review of an already reviewed invoice ('complete'/'partial' — exactly the
// case that carries an assigned expense).
//
// Being assigned to an expense/revenue used to disqualify a document here, and the Details tab
// redirected the reviewer to /expenses/edit/:id instead. That threw away the drawer and the
// already-rendered PDF to show a form the drawer can host itself — the reviewer clicked "Details"
// and got a full page rebuild. Assignment is therefore NOT a disqualifier any more; the re-review
// save path (useReReviewSave + reReviewSave marker) is what makes hosting it here correct.
//
// Shared by the tab content (incoming-invoice-details.tsx) and the drawer footer (its Save must
// delegate to the embedded review) so the two can't drift.
export const isEmbeddedInvoiceReview = (file, folderReadOnly) => {
  if (folderReadOnly) { return false }
  // orgaMAX Desktop owns the document: the API accepts nothing but notes and tags on it
  // (modules/file/desktop-owned-file.ts), so the review's Save — which posts
  // documentInvoiceDetails — is refused outright. Offering the form would be an invitation to type
  // into a mask that cannot save. The Details tab falls back to the legacy fields, which render
  // read-only for a desktop document anyway; the same refusal as a read-only folder above.
  if (isDesktopOwnedFile(file)) { return false }
  // A classification orgaMAX Web has no review for (contract) is never offered here.
  if (isReviewBlockedClassification(file)) { return false }
  // An assigned REVENUE is the one case the drawer must still stay out of: the drawer host mounts
  // the workspace hard as RECEIPT_TYPES.EXPENSE (document-invoice-review-drawer-tab.tsx), so
  // hosting a revenue here would review it in the wrong booking direction. Until the review path
  // for revenues exists (there is no createRevenueFromReviewedInvoice), such a document keeps its
  // pre-existing behaviour and is routed to its revenue instead (incoming-invoice-details.tsx).
  const assignedRevenue = Array.isArray(file?.revenue) ? file.revenue[0] : file?.revenue
  if (assignedRevenue?.id) { return false }
  return isPendingInvoiceReview(file) || isReReviewStatus(file?.documentInvoiceDetails?.reviewStatus)
}

// FLAG:useDocuAi — the DMS verdict for documents that will never produce an analysis result,
// written by processIndexingFinished from `indexingFinished.analysisEligibility`.
//
// The `kind` vocabulary is the DMS's (see docuai-routing-eligibility.getAnalysisOutcome); an
// unknown kind resolves to null so a future value from a newer DMS degrades to today's
// behaviour instead of rendering an empty badge. `reason` is carried for the tooltip only —
// nothing keys on it, so the DMS can add reasons without a client change.
export const getAnalysisEligibilityState = (file) => {
  const kind = file?.metaData?.analysisEligibility?.kind
  if (kind === 'containerIndexed') return INVOICE_ANALYSIS_STATE.CONTENT_INDEXED
  if (kind === 'notApplicable') return INVOICE_ANALYSIS_STATE.NOT_APPLICABLE
  return null
}

const deriveInvoiceAnalysisState = (file) => {
  if (!file) return null

  // FLAG:useDocuAi — AI analysis is running (DocuAI was dispatched, no result yet):
  // show the (blinking) ANALYZING badge. analysisInProgress is authoritative — it is set
  // synchronously when analysisStarted arrives and reliably cleared at EVERY terminal
  // (invoiceAnalysisReady / analysisFinished / analysisFailed), so we trust it directly.
  // We deliberately do NOT also require !documentInvoiceDetails: an invoice's review stub
  // can be created early (well before the DocuAI result), and gating on it would suppress
  // the blink for the whole (often 15–20s) analysis window. !ocr stays as the backstop for
  // the brief terminal window where the cleared flag and the ocr result cross paths.
  // FLAG:useDocuAi — `!ocr` alone used to be the rule, and it is wrong for a SECOND analysis:
  // „Erneut analysieren" runs on a document that already carries an `ocr` result from the first
  // run. This term therefore silenced the badge — the user triggered a run and saw no response
  // until it finished (observed live on file 52447:
  // analysisInProgress true, ocr present, no blink).
  //
  // `analysisDoneAt` answers the question correctly: it is written at EVERY terminal and cleared
  // when a new lifecycle starts (analyzeFile `startsNewLifecycle`). If it is absent while the flag
  // is set, the CURRENT run is still active — regardless of whether an older result is present.
  // The old `!ocr` remains as the first term: it still covers the initial analysis unchanged
  // (where neither ocr nor analysisDoneAt exists).
  //
  // The refinement is necessary to preserve the OLD
  // backstop: a stuck `analysisInProgress` NEXT TO
  // a result must not blink forever. Therefore, when results exist, a run counts as “running” only
  // if it is also DATED (`analysisStartedAt`) — exactly what a sanctioned new lifecycle does when
  // it resets the timestamp. An old, undated flag thus stays quiet as before; the age limit
  // (ANALYZING_MAX_MS) below catches an old DATED one.
  const hasPreviousResult = !!file.metaData?.ocr
  // A terminal lifecycle marker is stronger than a stale provider placeholder. The FILE:ANALYSED
  // handler can clear analysisInProgress synchronously while the list row still carries the old
  // `ocr.isRunning=true` until its asynchronous /meta refresh lands. Treating that mixed state as
  // running is exactly what kept the blue AI badge blinking after a quota refusal.
  //
  // A surgical list merge can also retain the PREVIOUS run's analysisDoneAt after a newer
  // analysisStartedAt arrives. Only call the timestamp terminal when it is not provably older than
  // the current start; malformed/missing timestamps stay conservative and preserve the old rule.
  const startedAtMs = file.metaData?.analysisStartedAt
    ? new Date(file.metaData.analysisStartedAt).getTime()
    : NaN
  const doneAtMs = file.metaData?.analysisDoneAt
    ? new Date(file.metaData.analysisDoneAt).getTime()
    : NaN
  const donePredatesCurrentStart = Number.isFinite(startedAtMs)
    && Number.isFinite(doneAtMs)
    && doneAtMs < startedAtMs
  const currentLifecycleDone = !!file.metaData?.analysisDoneAt && !donePredatesCurrentStart
  const lifecycleTerminated = file.metaData?.analysisInProgress === false
    || currentLifecycleDone
  const isAnalyzing = !lifecycleTerminated
    && file.metaData?.analysisInProgress
    && (!hasPreviousResult
      || !!file.metaData?.analysisStartedAt)
  if (isAnalyzing) {
    return file.metaData?.analysisRetrying
      ? INVOICE_ANALYSIS_STATE.RETRYING
      : INVOICE_ANALYSIS_STATE.ANALYZING
  }

  // FLAG:useDocuAi — THE KoSIT VERDICT TAKES PRECEDENCE (established 2026-08-16). Analysis and
  // conformance validation are two separate things: extraction reads the XML values correctly even
  // when those values are semantically invalid — that does not change the verdict “this e-invoice
  // is invalid”. So this verdict has a much greater consequence: an invalid e-invoice must be
  // REJECTED and must not be corrected first (which is why its fields are disabled in the review).
  // Therefore, “invalid” defeats BOTH states here that would otherwise win and would both make a
  // milder claim:
  //   analysisFailed      — orange would clearly read as “something went wrong, try again” rather
  //                         than “the document must not be booked in this form”.
  //   complete / partial  — review status only says someone looked at it; a green check next to an
  //                         invoice that must be rejected claims the opposite.
  // Deliberately expressed as two targeted refusals and NOT by moving the INVALID check to the top:
  // every other precedence remains untouched — in particular, an active run keeps blinking (also
  // via the secondary `ocr.isRunning` path), rather than showing red prematurely even though that
  // run may deliver a new validation result.
  const invalidEInvoice = hasInvalidValidationResult(file)

  // A backend/DocuAI failure says nothing about invoice validity. Keep it ahead
  // of the OCR/XML branches so a stopped in-flight placeholder cannot turn into
  // the red INVALID state.
  if (file.metaData?.analysisFailed === true) {
    return invalidEInvoice ? INVOICE_ANALYSIS_STATE.INVALID : INVOICE_ANALYSIS_STATE.FAILED
  }

  // FLAG:useDocuAi — the document is one that never produces an analysis result. Stamped by
  // the DMS at the routing point and delivered on `indexingFinished`
  // (processIndexingFinished), so the client mirrors no eligibility rule of its own — there
  // is exactly one place that decides what can be analysed, and it is in the DMS.
  //
  // Deliberately placed AFTER the analyzing and failed branches: while work is in flight the
  // blink is still the honest answer (it is what tells the user we are on it), and an
  // observed technical failure outranks a "nothing to do here". Everything below this point
  // is about invoice/OCR results, which these documents by definition do not have.
  const eligibility = getAnalysisEligibilityState(file)
  if (eligibility) return eligibility

  // FLAG:useDocuAi — a classification orgaMAX Web has no review for (contract) never gets an
  // invoice-review badge. Deliberately AFTER the analyzing branch above: while DocuAI is working
  // the blinking AI badge is correct for a contract too — it is only the FINISHED state that must
  // not claim "Zu prüfen". Returning null here leaves the tile to the static AI badge
  // (`aiProcessed`), i.e. "AI looked at this, there is nothing to review".
  if (isReviewBlockedClassification(file)) return null

  // hasGeneratedFiles means DMS produced artifacts (validation report, PDF report
  // PDF …) while analysis is still in flight — it is NOT itself invoice
  // analysis. Keep it OUT of hasXmlValidation so it can only ever drive the
  // ANALYZING state below, never the yellow REVIEW/COMPLETE clip. The yellow
  // clip must wait for real markers (KoSIT/xml validation, legacy OCR, or
  // documentInvoiceDetails review data).
  const hasXmlValidation = hasXmlInvoiceAnalysis(file)
  const hasDocuAiOcrAnalysis = hasLegacyOcrAnalysis(file) && hasDocuAiAnalysis(file)

  if (!hasXmlValidation && !hasLegacyOcrAnalysis(file) && !file.hasGeneratedFiles) return null

  // FLAG:useDocuAi — “reviewed” NEVER takes precedence over “invalid” (see `invalidEInvoice` above).
  if (file.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.COMPLETE) {
    return invalidEInvoice ? INVOICE_ANALYSIS_STATE.INVALID : INVOICE_ANALYSIS_STATE.COMPLETE
  }

  if (file.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.PARTIAL) {
    return invalidEInvoice ? INVOICE_ANALYSIS_STATE.INVALID : INVOICE_ANALYSIS_STATE.PARTIAL
  }

  if (file.metaData?.ocr?.isRunning) {
    if (!lifecycleTerminated) {
      // The in-flight placeholder means "still running" — and while a retry whisper is
      // present the honest running-state is RETRYING, not the plain blink. The drawer's
      // serialize path merges a FileAnalyze-derived ocr object onto metaData, so unlike
      // the list rows it never passes the !ocr gate above (found live in the T8 e2e:
      // list tile said "delayed" while the drawer said "In Analyse").
      return file.metaData?.analysisRetrying
        ? INVOICE_ANALYSIS_STATE.RETRYING
        : INVOICE_ANALYSIS_STATE.ANALYZING
    }
    // Explicit terminal truth wins over the stale isRunning bit. Fall through instead of
    // returning neutral: the same OCR object may already carry the real invoice values, whose
    // honest terminal state (REVIEW/COMPLETE) is derived below.
  }

  if (invalidEInvoice) {
    return INVOICE_ANALYSIS_STATE.INVALID
  }

  // FLAG:useDocuAi — `metaData.ocr.error` is an OBSERVED TECHNICAL FAILURE, not a verdict about the
  // document: it is set only in the legacy path — when the DMS request throws (analyzeFileLegacy
  // `dmsResult.error = true` around getOrCreateKeyValue), and since 2026-09-10 also when the DMS
  // answers a terminal failure with no data at all (`processingStatus` FAILED/UNKNOWN/NO_CONTRACT,
  // convertExtractions `resolveNatifRunState`) — which before that read as "still running" and left
  // the row blinking forever. Until 2026-08-16 it fell into the same red
  // INVALID bucket here as the KoSIT verdict — contrary to the rule already used everywhere else:
  // red indicates “this e-invoice is invalid”, while a DMS/DocuAI failure during analysis means
  // orange + „!" (FAILED). Combining the two tells the user the wrong thing — they look for a defect
  // in the receipt even though the analysis did not complete.
  //
  // Deliberately NOT moved up to `analysisFailed`, but kept exactly at its old position: the
  // preceding order (analyzing → analysisFailed → eligibility → COMPLETE/PARTIAL → isRunning)
  // therefore remains unchanged character for character. Only the answer for this one case changes.
  // `hasInvalidValidationResult` still comes first, so with BOTH markers the red invalid verdict
  // continues to win as before.
  if (file.metaData?.ocr?.error) {
    return INVOICE_ANALYSIS_STATE.FAILED
  }

  // FLAG:useDocuAi — a finished pre-switch legacy result drives no new review/complete state.
  // Keep ownership gating below running states and observed technical failures: those must
  // remain visible regardless of which provider produced the OCR marker.
  if (!hasXmlValidation && !hasDocuAiOcrAnalysis && !file.hasGeneratedFiles) return null

  const isReviewed = hasReviewedInvoiceAnalysis(file)
  // Generated artifacts but no real analysis data yet → still ANALYZING, never
  // the yellow REVIEW clip. Gate on the actual markers (xml/KoSIT validation,
  // legacy OCR, documentInvoiceDetails) rather than validationResult alone, so a
  // freshly-uploaded file's auto-created expense (with unchecked OCR values)
  // cannot fall through to checkOcrValues and flip the clip to REVIEW early.
  if (file.hasGeneratedFiles && !hasXmlValidation && !hasDocuAiOcrAnalysis
    && !file.documentInvoiceDetails && !isReviewed) {
    if (lifecycleTerminated) return null
    return INVOICE_ANALYSIS_STATE.ANALYZING
  }

  const ocrState = checkOcrValues(file.expense, file.revenue, file)
  if (!isReviewed && ocrState.hasUncheckedOcrValues) {
    return INVOICE_ANALYSIS_STATE.REVIEW
  }

  if (hasXmlValidation && isValidValidationValue(file.validationResult?.valid)) {
    return isReviewed ? INVOICE_ANALYSIS_STATE.COMPLETE : INVOICE_ANALYSIS_STATE.REVIEW
  }

  if (hasDocuAiOcrAnalysis) {
    return isReviewed ? INVOICE_ANALYSIS_STATE.COMPLETE : INVOICE_ANALYSIS_STATE.REVIEW
  }

  // Honour the reviewed marker in the fallback too: a reviewed invoice resolves
  // to COMPLETE (green), not REVIEW. The document list carries the reviewed flag
  // via metaData.invoiceAnalysisReviewed (hasReviewedInvoiceAnalysis) even though
  // it doesn't include documentInvoiceDetails.reviewStatus, so without this a
  // saved/reviewed invoice would keep showing the yellow clip in the list.
  return isReviewed ? INVOICE_ANALYSIS_STATE.COMPLETE : INVOICE_ANALYSIS_STATE.REVIEW
}

export const getInvoiceAnalysisState = (file) => {
  const state = deriveInvoiceAnalysisState(file)
  const isRunningState = state === INVOICE_ANALYSIS_STATE.ANALYZING
    || state === INVOICE_ANALYSIS_STATE.RETRYING

  if (!isRunningState) {
    // Left every running-state path → forget the client backstop timestamp (keeps the Map bounded).
    forgetAnalyzingBackstop(file?.id)
    return state
  }

  // FLAG:useDocuAi — one absolute backstop for EVERY path that can render a running state. Besides
  // the authoritative analysisInProgress lifecycle, drawer/list payloads can briefly expose only
  // ocr.isRunning or generated artifacts. Keeping the cap here prevents either fallback from
  // bypassing the 45-second promise.
  //
  // The retrying whisper both (a) switches the optics to the calm RETRYING state and (b) EXTENDS
  // the anchor: DocuAI is provably alive and its backoff gaps reach 30 min, so the cap counts from
  // the newest of start, last whisper and announced next retry.
  const retrying = file?.metaData?.analysisRetrying
  const anchors = [
    analyzingStartMs(file),
    retrying?.at ? new Date(retrying.at).getTime() : NaN,
    retrying?.nextRetryAt ? new Date(retrying.nextRetryAt).getTime() : NaN,
  ].filter((ms) => ms != null && Number.isFinite(ms))
  const anchorMs = anchors.length ? Math.max(...anchors) : null
  const stale = anchorMs != null && (Date.now() - anchorMs) > ANALYZING_MAX_MS
  if (!stale) return state

  // Stale: no terminal arrived and the watchdog hasn't bounded the DB flag yet. HALT the blink
  // and resolve to a NEUTRAL tile (no badge) — we never observed a result, so we don't claim a
  // failure (red). Keep the timestamp while the stale signal remains so subsequent renders cannot
  // restart the clock; it is forgotten once the row really leaves every running path.
  return null
}

export const getInvoiceAnalysisLabel = (state) => {
  switch (state) {
    case INVOICE_ANALYSIS_STATE.ANALYZING:
      return lang.toAnalyze
    case INVOICE_ANALYSIS_STATE.RETRYING:
      return lang.documents.invoiceAnalysisRetrying
    case INVOICE_ANALYSIS_STATE.COMPLETE:
      return lang.documents.invoiceAnalysisComplete
    case INVOICE_ANALYSIS_STATE.FAILED:
      return lang.documents.invoiceAnalysisFailed
    case INVOICE_ANALYSIS_STATE.INVALID:
      return lang.documents.invoiceAnalysisInvalid
    case INVOICE_ANALYSIS_STATE.PARTIAL:
      return lang.documents.invoiceAnalysisPartial
    case INVOICE_ANALYSIS_STATE.REVIEW:
      return lang.documents.invoiceAnalysisReview
    case INVOICE_ANALYSIS_STATE.VALID:
      return lang.documents.invoiceAnalysisValid
    case INVOICE_ANALYSIS_STATE.ABORTED:
      return lang.documents.invoiceAnalysisAborted
    case INVOICE_ANALYSIS_STATE.NO_INVOICE:
      return lang.documents.invoiceAnalysisNoInvoice
    case INVOICE_ANALYSIS_STATE.NOT_APPLICABLE:
      return lang.documents.analysisNotApplicable
    case INVOICE_ANALYSIS_STATE.CONTENT_INDEXED:
      return lang.documents.analysisContentIndexed
    default:
      return ''
  }
}
